import React, { createContext, useContext, useState, useEffect } from 'react';

interface TimezoneContextType {
  timezone: string;
  setTimezone: (tz: string) => void;
  formatDate: (date: string | Date, options?: Intl.DateTimeFormatOptions) => string;
  getTodayKey: () => string; // Retorna YYYY-MM-DD no fuso escolhido
}

const TimezoneContext = createContext<TimezoneContextType>({
  timezone: 'UTC',
  setTimezone: () => {},
  formatDate: () => '',
  getTodayKey: () => ''
});

export const TimezoneProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  // 1. Tenta pegar do localStorage
  // 2. Se não tiver, pega do navegador
  // 3. Fallback para UTC
  const [timezone, setTimezoneState] = useState<string>(() => {
    const saved = localStorage.getItem('cognitya_user_timezone');
    if (saved) return saved;
    try {
      return Intl.DateTimeFormat().resolvedOptions().timeZone;
    } catch (e) {
      return 'UTC';
    }
  });

  const setTimezone = (tz: string) => {
    setTimezoneState(tz);
    localStorage.setItem('cognitya_user_timezone', tz);
  };

  const formatDate = (date: string | Date, options?: Intl.DateTimeFormatOptions) => {
    const d = typeof date === 'string' ? new Date(date) : date;
    return d.toLocaleString('pt-BR', {
      ...options,
      timeZone: timezone
    });
  };

  const getTodayKey = () => {
    return new Date().toLocaleDateString('en-CA', { timeZone: timezone });
  };

  return (
    <TimezoneContext.Provider value={{ timezone, setTimezone, formatDate, getTodayKey }}>
      {children}
    </TimezoneContext.Provider>
  );
};

export const useTimezone = () => useContext(TimezoneContext);

export const AVAILABLE_TIMEZONES = [
  { value: 'America/Sao_Paulo', label: '🇧🇷 Brasília (Padrão)' },
  { value: 'America/New_York', label: '🇺🇸 EUA - Costa Leste (NY, Miami, Orlando)' },
  { value: 'America/Los_Angeles', label: '🇺🇸 EUA - Costa Oeste (LA, Califórnia)' },
  { value: 'America/Chicago', label: '🇺🇸 EUA - Central (Houston, Texas)' },
  { value: 'Europe/Lisbon', label: '🇵🇹 Portugal (Lisboa)' },
  { value: 'Europe/London', label: '🇬🇧 Reino Unido (Londres)' },
  { value: 'Europe/Berlin', label: '🇪🇺 Europa Central (Alemanha, Itália, Espanha)' },
  { value: 'Asia/Tokyo', label: '🇯🇵 Japão (Tóquio, Nagoia)' },
  { value: 'America/Asuncion', label: '🇵🇾 Paraguai (Assunção)' },
  { value: 'America/Argentina/Buenos_Aires', label: '🇦🇷 Argentina (Buenos Aires)' },
  { value: 'UTC', label: '🌐 UTC (Universal)' }
];